import { AcceptInvitationResult, InvitationPreview } from '../types';
import { delay } from './delay';
import {
  db,
  findActiveMembershipByUserId,
  findBusinessById,
} from './db';
import { ERR_NOT_FOUND, ERR_VALIDATION } from './errors';
import { readUserIdFromToken, hasToken } from './auth-utils';

const issueToken = (suffix: string) =>
  `mock.${suffix}.${Date.now().toString(36)}`;

export const invitationMock = {
  preview: async (token: string): Promise<InvitationPreview> => {
    await delay(60, 180);
    const inv = db.invitations.find((i) => i.token === token);
    if (!inv) throw ERR_NOT_FOUND('Invitation');
    if (inv.status !== 'PENDING')
      throw ERR_VALIDATION(`INVITATION_${inv.status}`);
    if (Date.parse(inv.expiresAt) < Date.now())
      throw ERR_VALIDATION('INVITATION_EXPIRED');
    const business = findBusinessById(inv.businessId);
    return {
      invitationId: inv.id,
      businessId: inv.businessId,
      businessName: business?.name ?? 'Business',
      invitedEmail: inv.invitedEmail,
      expiresAt: inv.expiresAt,
    };
  },

  accept: async (token: string): Promise<AcceptInvitationResult> => {
    await delay();
    if (!hasToken())
      throw ERR_VALIDATION('Login required to accept invitation');
    const userId = readUserIdFromToken();
    const inv = db.invitations.find((i) => i.token === token);
    if (!inv) throw ERR_NOT_FOUND('Invitation');
    if (inv.status !== 'PENDING')
      throw ERR_VALIDATION(`INVITATION_${inv.status}`);
    if (Date.parse(inv.expiresAt) < Date.now())
      throw ERR_VALIDATION('INVITATION_EXPIRED');
    const existing = findActiveMembershipByUserId(userId);
    if (existing) throw ERR_VALIDATION('USER_ALREADY_IN_ANOTHER_BUSINESS');
    inv.status = 'ACCEPTED';
    inv.acceptedByUserId = userId;
    inv.acceptedAt = new Date().toISOString();
    db.memberships.push({
      id: `mem-${Date.now().toString(36)}`,
      businessId: inv.businessId,
      userId,
      role: 'MEMBER',
      status: 'ACTIVE',
      joinedAt: new Date().toISOString(),
      invitedByUserId: inv.invitedByUserId,
    });
    const business = findBusinessById(inv.businessId);
    if (!business) throw ERR_NOT_FOUND('Business');
    return {
      businessId: inv.businessId,
      businessName: business.name,
      role: 'MEMBER',
      status: 'ACCEPTED',
      newToken: {
        accessToken: issueToken(userId),
        refreshToken: issueToken(userId + '.refresh'),
        expiresIn: 60 * 60,
      },
    };
  },
};
